import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import Modal from '../../components/Modal';
import Pagination from '../../components/common/Pagination';
import ManagementHeader from './ManagementHeader';
import { fetchUsersForManagement, deleteUser } from '../../services/api';
import { hasPermission } from '../../services/authService';
import { useTranslation } from '../../context/LanguageContext';
import './management.css';

const DASH = '—';
const show = (v) => (v == null || v === '' || v === '-' ? DASH : v);

// 상태 매핑 (API → 화면 한글 키)
const STATUS_KO = { ACTIVE: '활성', INACTIVE: '비활성', LOCKED: '잠김' };
const STATUS_BADGE = { '활성': 'success', '비활성': 'neutral', '잠김': 'error' };

// 날짜 포맷 (2021. 12. 12. 형식)
const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

// 날짜시간 포맷 (2021. 12. 12. 14:30 형식)
const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return null;
    const date = new Date(dateTimeString);
    return `${date.toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' })} ${date.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
};

// GET /management/users 응답 → 화면 행
const transformUserData = (user) => {
    let roleGroup;
    if (user.roleGroup) {
        roleGroup = {
            id: user.roleGroup.id,
            name: user.roleGroup.name,
            code: user.roleGroup.code,
            description: user.roleGroup.description,
            permissions: user.roleGroup.permissions || []
        };
    } else {
        // roleGroup 이 없는 경우 (하위 호환성)
        let roleName;
        let roleCode;
        if (user.isSuperAdmin) {
            roleName = '슈퍼 관리자';
            roleCode = 'SUPER_ADMIN';
        } else if (user.isCompanyAdmin) {
            roleName = '시스템 관리자';
            roleCode = 'ADMIN';
        } else {
            roleName = '일반 사용자';
            roleCode = 'NORMAL';
        }
        roleGroup = {
            id: user.isSuperAdmin ? 0 : (user.isCompanyAdmin ? 1 : 2),
            name: roleName,
            code: roleCode,
            description: '',
            permissions: []
        };
    }

    return {
        id: user.id,
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || null,
        roleGroup,
        department: user.department || null,
        position: user.position || null,
        status: STATUS_KO[user.status] || '비활성',
        lastLogin: formatDateTime(user.lastLogin),
        joinDate: formatDate(user.createdAt),
        isCompanyAdmin: user.isCompanyAdmin,
        isSuperAdmin: user.isSuperAdmin,
        companyName: user.companyName
    };
};

function UserListPage() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [selectedRole, setSelectedRole] = useState('전체');
    const [searchQuery, setSearchQuery] = useState('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [users, setUsers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    // 페이지네이션 상태
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);

    // 권한 체크
    const canCreate = hasPermission('user.create');
    const canUpdate = hasPermission('user.update');
    const canDelete = hasPermission('user.delete');

    const getStatusLabel = (status) => {
        const map = {
            '활성': t('pages.management.userList.status.active'),
            '비활성': t('pages.management.userList.status.inactive'),
            '잠김': t('pages.management.userList.status.locked'),
        };
        return map[status] || status;
    };

    const getRoleLabel = (roleName) => {
        const map = {
            '슈퍼 관리자': t('pages.management.userList.role.superAdmin'),
            '시스템 관리자': t('pages.management.userList.role.systemAdmin'),
            '일반 사용자': t('pages.management.userList.role.normalUser'),
        };
        return map[roleName] || roleName;
    };

    const loadUsers = useCallback(async () => {
        const data = await fetchUsersForManagement();
        const rows = (Array.isArray(data) ? data : []).map(transformUserData);
        setAllUsers(rows);
        setUsers(rows);
    }, []);

    // API에서 회원 목록 가져오기
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                await loadUsers();
            } catch (err) {
                console.error('회원 목록 조회 실패:', err);
                setAllUsers([]);
                setUsers([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [loadUsers]);

    // 필터링 로직
    useEffect(() => {
        let filtered = [...allUsers];

        if (selectedRole !== '전체') {
            filtered = filtered.filter(user => user.roleGroup.name === selectedRole);
        }

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filtered = filtered.filter(user =>
                user.name.toLowerCase().includes(query) ||
                user.email.toLowerCase().includes(query)
            );
        }

        setUsers(filtered);
        setPage(0); // 필터 변경시 첫 페이지로
    }, [selectedRole, searchQuery, allUsers]);

    // 페이지네이션 적용
    const paginatedUsers = useMemo(() => {
        const startIndex = page * size;
        return users.slice(startIndex, startIndex + size);
    }, [users, page, size]);

    const totalElements = users.length;
    const totalPages = Math.ceil(totalElements / size);

    const handleSizeChange = (newSize) => {
        setSize(newSize);
        setPage(0);
    };

    const permissionLabels = {
        dashboard: t('pages.management.userList.permissionLabel.dashboard'),
        monitoring: t('pages.management.userList.permissionLabel.monitoring'),
        factory: t('pages.management.userList.permissionLabel.factory'),
        equipment: t('pages.management.userList.permissionLabel.equipment'),
        user: t('pages.management.userList.permissionLabel.user'),
        role: t('pages.management.userList.permissionLabel.role'),
        system: t('pages.management.userList.permissionLabel.system')
    };

    const getActivePermissions = (permissions) => {
        // 백엔드에서 받은 권한 배열인 경우
        if (Array.isArray(permissions)) {
            return permissions.map(perm => perm.name);
        }
        // 기존 객체 형식 (하위 호환성)
        if (typeof permissions === 'object' && permissions !== null) {
            return Object.entries(permissions)
                .filter(([, value]) => value)
                .map(([key]) => permissionLabels[key] || key);
        }
        return [];
    };

    const handleDeleteClick = (user) => {
        setSelectedUser(user);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteUser(selectedUser.id);
            alert(t('pages.management.userList.alertDeleted', { name: selectedUser.name }));
            setIsDeleteModalOpen(false);
            await loadUsers(); // 목록 새로고침
        } catch (error) {
            console.error('회원 삭제 실패:', error);
            alert(t('pages.management.userList.alertDeleteFail'));
        }
    };

    const colCount = 9;

    return (
        <MainLayout>
            <div className="mg-page">
                <ManagementHeader rootLabel={t('sidebar.user.title')} icon="manage_accounts" crumbs={[{ label: t('sidebar.user.list') }]}>
                    {canCreate && (
                        <button type="button" className="mg-btn primary" onClick={() => navigate('/user/register')}>
                            <span className="material-symbols-outlined">add</span>
                            {t('pages.management.userList.addBtn')}
                        </button>
                    )}
                </ManagementHeader>

                <div className="mg-panel fill">
                    <div className="mg-toolbar">
                        <label className="mg-field">
                            {t('pages.management.userList.label.role')}
                            <select className="mg-select" value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)}>
                                <option value="전체">{t('pages.management.userList.roleOption.all')}</option>
                                <option value="시스템 관리자">{t('pages.management.userList.roleOption.systemAdmin')}</option>
                                <option value="일반 사용자">{t('pages.management.userList.roleOption.normalUser')}</option>
                            </select>
                        </label>
                        <div className="mg-toolbar-right">
                            <div className="mg-search">
                                <input
                                    type="text"
                                    className="mg-input"
                                    placeholder={t('pages.management.userList.searchPlaceholder')}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                <span className="material-symbols-outlined">search</span>
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="mg-loading">{t('pages.management.userList.loading')}</div>
                    ) : (
                        <>
                            <div className="mg-table-wrap">
                                <table className="mg-table">
                                    <thead>
                                        <tr>
                                            <th className="left">{t('pages.management.userList.col.user')}</th>
                                            <th>{t('pages.management.userList.col.position')}</th>
                                            <th>{t('pages.management.userList.col.department')}</th>
                                            <th>{t('pages.management.userList.col.status')}</th>
                                            <th>{t('pages.management.userList.col.lastLogin')}</th>
                                            <th>{t('pages.management.userList.col.joinDate')}</th>
                                            <th>{t('pages.management.userList.col.roleGroup')}</th>
                                            <th>{t('pages.management.userList.col.permissions')}</th>
                                            <th>{t('pages.management.userList.col.action')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedUsers.length === 0 && (
                                            <tr className="mg-empty-row"><td className="mg-empty" colSpan={colCount}>{t('common.noData')}</td></tr>
                                        )}
                                        {paginatedUsers.map((user) => {
                                            const perms = getActivePermissions(user.roleGroup.permissions);
                                            return (
                                                <tr key={user.id} onClick={() => navigate(`/user/${user.id}`)}>
                                                    <td className="left">
                                                        <div className="mg-name">
                                                            <span className="mg-avatar">{user.name.charAt(0)}</span>
                                                            <div>
                                                                <div className="mg-name-main">{user.name}</div>
                                                                <div className="mg-name-sub">{user.email}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>{show(user.position)}</td>
                                                    <td>{show(user.department)}</td>
                                                    <td>
                                                        <span className={`mg-badge ${STATUS_BADGE[user.status] || 'neutral'}`}>{getStatusLabel(user.status)}</span>
                                                    </td>
                                                    <td className={user.lastLogin ? '' : 'mg-muted'}>{show(user.lastLogin)}</td>
                                                    <td className={user.joinDate ? '' : 'mg-muted'}>{show(user.joinDate)}</td>
                                                    <td>
                                                        <div className="mg-name-main">{getRoleLabel(user.roleGroup.name)}</div>
                                                        <div className="mg-name-sub">{user.roleGroup.code}</div>
                                                    </td>
                                                    <td>
                                                        {perms.length === 0 ? <span className="mg-muted">{DASH}</span> : (
                                                            <span className="mg-tags">
                                                                {perms.slice(0, 2).map((perm, i) => <span key={`${perm}-${i}`} className="mg-tag">{perm}</span>)}
                                                                {perms.length > 2 && <span className="mg-tag more">+{perms.length - 2}</span>}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="mg-actions" onClick={(e) => e.stopPropagation()}>
                                                            {canUpdate && (
                                                                <button type="button" className="mg-icon-btn" onClick={() => navigate(`/user/${user.id}?edit=true`)}>
                                                                    <span className="material-symbols-outlined">edit</span>
                                                                </button>
                                                            )}
                                                            {canDelete && (
                                                                <button type="button" className="mg-icon-btn danger" onClick={() => handleDeleteClick(user)}>
                                                                    <span className="material-symbols-outlined">delete</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            {totalElements > 0 && (
                                <Pagination
                                    page={page}
                                    totalPages={totalPages}
                                    totalElements={totalElements}
                                    size={size}
                                    onPageChange={setPage}
                                    onSizeChange={handleSizeChange}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>

            <Modal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDeleteConfirm}
                title={t('pages.management.userList.deleteModal.title')}
                message={selectedUser ? t('pages.management.userList.deleteModal.message', { name: selectedUser.name }) : ''}
                confirmText={t('pages.management.userList.deleteModal.confirm')}
                cancelText={t('pages.management.userList.deleteModal.cancel')}
                type="danger"
            />
        </MainLayout>
    );
}

export default UserListPage;
